import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {interval, Observable, Subject} from 'rxjs';
import {Info} from './model/info';
import {Dotaznik} from './model/dotaznik';
import {Diskusia} from './model/diskusia';
import {Aplikacia} from './model/aplikacia';
import {Messages} from './model/messages';
import {Job} from './model/job';
import {Poplatok} from './model/poplatok';
import {Studium} from './model/studium';
import {Message} from './model/message';
import * as FileSaver from 'file-saver';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmDialogComponent, Error, ErrorDialogComponent, OkDialogComponent} from 'ais-common';
import {FlashMessage} from './model/flashMessage';
import {Zalozka} from './model/zalozka';
import {ZalozkaObsah} from './model/zalozkaObsah';
import {Change} from './model/change';
import {TranslateService} from '@ngx-translate/core';
import {ComponentType} from '@angular/cdk/portal';
import {HarmonogramItem} from './model/HarmonogramItem';
import {ZapisnyList} from './model/zapisnyList';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  infoData = new Subject<Info>();
  dotaznikyData = new Subject<Dotaznik[]>();
  diskusiaData = new Subject<Diskusia[]>();
  aplikacieData = new Subject<Aplikacia[]>();
  messagesData = new Subject<Messages[]>();
  jobsData = new Subject<Job[]>();
  poplatkyData = new Subject<Poplatok[]>();
  studiaData  = new Subject<Studium[]>();
  flashMessagesSubject  = new Subject<FlashMessage[]>();
  predpisDownloaded  = new Subject<boolean>();
  zalozkySubject = new Subject<Zalozka[]>();
  redSubject = new Subject<boolean>();
  harmonogramSubject = new Subject<HarmonogramItem[]>();

  onDotaznikyClickSubject = new Subject<boolean>();
  onPoplatkyClickSubject = new Subject<boolean>();
  onMessagesClickSubject = new Subject<boolean>();
  onJobMarketClickSubject = new Subject<boolean>();
  onDiskusiaClickSubject = new Subject<boolean>();

  private aplikacie: Aplikacia[];
  private jobs: Job[];
  private studium: Studium;
  private messages: Messages[];
  private dotazniky: Dotaznik[];
  private poplatky: Poplatok[];
  private studia: Studium[];
  private info: Info;
  private flashMessages: FlashMessage[];
  private zalozky: Zalozka[];
  private lastCheckTime = '';
  private red = false;



  constructor(
    private http: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    public dialog: MatDialog,
    private translate: TranslateService,
    private snackBar: MatSnackBar
  ) { }

  fetchInfo() {
    this.http
      .get<Info>('/ais/rest/portal/users/info')
      .subscribe((data) => {
        this.info = data;
        this.infoData.next(data);
        setTimeout(() => {
          this.infoData.next(data);
          }, 2000);
      });
  }

  fetchDotazniky() {
    if (this.dotazniky) {
      this.dotaznikyData.next(this.dotazniky);
    } else {
      this.http
        .get<Dotaznik[]>('/ais/rest/portal/studium/dotazniky')
        .subscribe((data) => {
          this.dotazniky = data;
          this.dotaznikyData.next(data);
        });
    }
  }

  fetchDiskusia() {
    this.http
      .get<Diskusia[]>('/ais/rest/portal/diskusia/list')
      .subscribe((data) => {
        this.diskusiaData.next(data);
      });
  }

  fetchAplikacie() {
    if (this.aplikacie) {
      this.aplikacieData.next(this.aplikacie);
    } else {
      this.http
        .get<Aplikacia[]>('/ais/rest/portal/portal/menu/student')
        .subscribe((data) => {
          this.aplikacie = data;
          this.aplikacieData.next(data);
        });
    }
  }

  fetchMessages(force: boolean = false) {
    if (this.messages && force === false) {
      this.messagesData.next(this.messages);
    } else {
      this.http
        .get<Messages[]>('/ais/rest/portal/messages/list2')
        .subscribe((data) => {
          this.messages = data;
          this.messagesData.next(data);
        });
    }
  }

  fetchJobs() {
    if (this.jobs) {
      this.jobsData.next(this.jobs);
    } else {
      this.http
        .get<Job[]>('/ais/rest/portal/pracovne-ponuky/list')
        .subscribe((data) => {
          this.jobs = data;
          this.jobsData.next(data);
        });
    }
  }

  fetchPoplatky(force: boolean = false) {
    if (this.poplatky && force === false) {
      this.poplatkyData.next(this.poplatky);
    } else {
      this.http
        .get<Poplatok[]>('/ais/rest/portal/portal/osoba/poplatky')
        .subscribe((data) => {
          this.poplatky = data;
          this.poplatkyData.next(data);
        });
    }
  }

  fetchStudia(force: boolean = false)
  {
    if (this.studia && force === false) {
      this.studiaData.next(this.studia);
    } else {
      this.http
        .get<Studium[]>('/ais/rest/portal/studium/list', {headers: {'api-version': '2024-05-10'}})
        .subscribe((data) => {
          this.studia = data;
          this.studiaData.next(data);
        });
    }
  }

  fetchFlashMessages(force: boolean = false) {
    if (this.flashMessages && force === false) {
      this.flashMessagesSubject.next(this.flashMessages);
    } else {
      this.http
        .get<FlashMessage[]>('/ais/rest/portal/flash/list')
        .subscribe((data) => {
          this.flashMessages = data;
          this.flashMessagesSubject.next(data);
        });
    }
  }

  fetchZalozky() {
    if (this.zalozky) {
      this.zalozkySubject.next(this.zalozky);
    } else {
      this.http
        .get<Zalozka[]>('/ais/rest/portal/portal/zalozky')
        .subscribe((data) => {
          this.zalozky = data;
          this.zalozkySubject.next(data);
        });
    }
  }

  openPortalAppWindow(app: string, id: any) {
    const url = '/ais/servlets/WebUIServlet?appClassName=ais.gui.common.PortalApp&target=' + app
      + '&kodAplikacie=Portal&id=' + id + '&viewer=web&uiLang=' + this.locale.toUpperCase();
    open(url, '_blank', 'channelmode=no,menubar=no,toolbar=no,status=yes,resizable=yes,width=1024,height=768');
  }

  openAppWindow(app: Aplikacia) {
    const url = '/ais/servlets/WebUIServlet?appClassName=' + app.className +
      '&kodAplikacie=' + app.appCode + '&viewer=web&uiLang=' + this.locale.toUpperCase();
    open(url, '_blank', 'channelmode=no,menubar=no,toolbar=no,status=yes,resizable=yes,width=1024,height=768');
  }

  openAppByMessage(m: Message) {
    console.debug(m);
    open(m.aplikaciaUrl, '_blank', 'channelmode=no,menubar=no,toolbar=no,status=yes,resizable=yes,width=1024,height=768');
  }

  downloadMessageDocument(message: Message) {
    this.http
      .post('/ais/rest/portal/documents/download/' + message.id, {}, {observe: 'events', responseType: 'arraybuffer'})
      .subscribe((res) => {
        if (res instanceof HttpResponse) {
          const blob = new Blob([res.body], {type: res.headers.get('Content-Type')});
          const fileName = 'document-' + message.id + '.' + res.headers.get('ais-file-suffix');
          FileSaver.saveAs(blob, fileName);
        }
      });
  }

  setStudium(studium: Studium) {
    this.studium = studium;
  }

  getStudium(): Studium {
    return this.studium;
  }

  downloadPoplatokPredpis(id: number, message: boolean = false)
  {
    this.http
      .post('/ais/rest/portal/portal/osoba/predpis/' + id + '?message=' + message, {}, {observe: 'events', responseType: 'arraybuffer'})
      .subscribe({
        next: (res) => {
          if (res instanceof HttpResponse) {
            const blob = new Blob([res.body], {type: 'application/pdf'});
            const fileName = 'predpis-' + id + '.pdf';
            FileSaver.saveAs(blob, fileName);
            this.predpisDownloaded.next(true);
          }
      },
      error: (error: Error) => {
        this.openErrorDialog(this.translate.instant('Predpis sa nepodarilo stiahnuť.'));
        this.predpisDownloaded.next(false);
      }
    });
  }

  openErrorDialog(msg: string) {
    this.dialog.open(ErrorDialogComponent, {
      panelClass: 'alert-danger',
        data: {message: msg},
        width: '500px',
      });
  }

  openConfirmDialog(msg: string): Observable<string> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'alert-warning',
      data: {message: msg},
      width: '500px',
    });
    return dialogRef.afterClosed();
  }

  openConfirmDialogReturnsDialog(msg: string): MatDialogRef<ConfirmDialogComponent> {
    const dialogRef = this.dialog.open(
        ConfirmDialogComponent, {
          panelClass: 'alert-warning',
          data: {message: msg},
          width: '500px'
        }
    );
    return dialogRef;
  }

  openOkDialog(msg: string): Observable<string> {
    const dialogRef = this.dialog.open(OkDialogComponent, {
      panelClass: 'alert-success',
      data: {message: msg},
      width: '500px',
    });
    return dialogRef.afterClosed();
  }

  getPlatobnaBrana() {
    return this.info.platobnaBrana;
  }

  fetchZalozka(id: string): Observable<ZalozkaObsah[]> {
    return this.http.get<ZalozkaObsah[]>('/ais/rest/portal/portal/obsah?id=' + id);
  }

  zrusZapisnyList(id: number) {
    return this.http.get<unknown>('/ais/rest/portal/zapisne-listy/zrusit?id=' + id);
  }

  startChangesCheck() {
    const timerSubscription = interval(60000)
      .subscribe((val) => {
        console.log('changes check');

        this.http.get<Change>('/ais/rest/apps/check-session/changes', {headers: {LastCheckTime: this.lastCheckTime}})
          .subscribe((res) => {
            console.log(res);

            this.lastCheckTime = res.lastCheckTime;

            if (res.flashMessages) {
              this.fetchFlashMessages(true);
            }

            if (res.messages) {
              this.fetchMessages(true);
            }

            if (res.poplatky) {
              this.fetchPoplatky(true);
            }

            if (res.slavnosti.length > 0) {
              this.fetchStudia(true);
            }

            if (res.statneSkusky.length > 0) {
              this.fetchStudia(true);
            }

            if (res.studia.length > 0) {
              this.fetchStudia(true);
            }

            if (res.zapisneListy.length > 0) {
              this.fetchStudia(true);
            }

            if (res.zaverecnePrace.length > 0) {
              this.fetchStudia(true);
            }
          });

      });
  }

  getRed() {
    return this.red;
  }

  setRed(red: boolean) {
    this.red = red;
    this.redSubject.next(this.red);
  }

  saveUserSettings() {
    this.http.post('/ais/rest/portal/users/settings', {
      idZobrazenie: 2,
      kodJazyk: this.locale.toUpperCase()
    })
      .subscribe(
      () => {},
      () => {},
      () => {}
    );
  }

  openDialog(cmp: ComponentType<any>, input: any)
  {
    return this.dialog.open(cmp, {data: input});
  }

  retrieveHarmonogram(zapisnyList: ZapisnyList) {
    this.http.get<HarmonogramItem[]>(
        '/ais/rest/portal/student-predmety/upozornenia/' + zapisnyList.id)
        .subscribe({
            next: (data) => {
                this.harmonogramSubject.next(data);
            },
            error: (error) => {
                console.error(error);
            },
            complete: () => {
            }
        });
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, '', {
      duration: 2000,
      panelClass: 'app-snack-bar'
    });
  }

    getStudiumById(id: number) {
        return this.studia.find((s) => s.id === id);
    }
}
